import { useEffect, useRef, useState } from "react";
import { EditorView } from "@codemirror/view";
import {
  startDiff,
  appendDiff,
  finishDiff,
  rejectDiff,
  buildAcceptTransaction,
  inlineDiffField,
} from "./InlineDiffExtension";
import type { DiffState } from "./InlineDiffExtension";
import { startEditorEdit, MeterEvent } from "../hooks/useEditorEdit";
import { useEditorSettings } from "../hooks/useEditorSettings";

function useDiffStateStatus(view: EditorView | null, open: boolean): DiffState["status"] {
  const [status, setStatus] = useState<DiffState["status"]>("idle");
  useEffect(() => {
    if (!view || !open) return;
    let raf = 0;
    const tick = () => {
      const newStatus = view.state.field(inlineDiffField).status;
      setStatus((cur) => (cur !== newStatus ? newStatus : cur));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [view, open]);
  return status;
}

interface Props {
  view: EditorView | null;
  filePath: string;
  fileContent: string;
  open: boolean;
  onClose: () => void;
  onMeterUpdate: (m: MeterEvent) => void;
}

export function CmdKOverlay({ view, filePath, fileContent, open, onClose, onMeterUpdate }: Props) {
  const [prompt, setPrompt] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [blocked, setBlocked] = useState<string[] | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const streamRef = useRef<ReturnType<typeof startEditorEdit> | null>(null);
  const { settings, addSpend, hardCapReached } = useEditorSettings();
  const diffStatus = useDiffStateStatus(view, open);

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (view) view.dispatch({ effects: rejectDiff.of() });
        if (streamRef.current) streamRef.current.abort();
        onClose();
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        if (!view) return;
        const s = view.state.field(inlineDiffField);
        const tx = buildAcceptTransaction(s);
        if (tx) {
          view.dispatch(tx);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, view, onClose]);

  function startStream(useOverride = false) {
    if (!view) return;
    if (hardCapReached) {
      onClose();
      return;
    }
    const sel = view.state.selection.main;
    const selFrom = sel.from;
    const selTo = sel.to;
    setBlocked(null);
    setStreaming(true);
    view.dispatch({ effects: startDiff.of({ selFrom, selTo }) });
    const stream = startEditorEdit({
      provider: settings.provider,
      model: settings.model,
      filePath,
      fileContent,
      selectionStart: selFrom,
      selectionEnd: selTo,
      prompt,
      localBaseUrl: settings.provider === "local" ? settings.localBaseUrl : undefined,
      dlpOverride: useOverride,
    });
    streamRef.current = stream;
    stream.onDelta((c) => view.dispatch({ effects: appendDiff.of({ chunk: c }) }));
    stream.onUsage((u) => {
      addSpend(u.cents);
      onMeterUpdate(u);
    });
    stream.done
      .then(() => {
        view.dispatch({ effects: finishDiff.of() });
        setStreaming(false);
      })
      .catch((e) => {
        setStreaming(false);
        view.dispatch({ effects: rejectDiff.of() });
        if (e && Array.isArray(e.blocked)) setBlocked(e.blocked);
      });
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!prompt.trim() || streaming || !view) return;
    startStream(false);
  }

  function handleAccept() {
    if (!view) return;
    const s = view.state.field(inlineDiffField);
    const tx = buildAcceptTransaction(s);
    if (tx) {
      view.dispatch(tx);
      onClose();
    }
  }

  function handleReject() {
    if (view) view.dispatch({ effects: rejectDiff.of() });
    if (streamRef.current) streamRef.current.abort();
    onClose();
  }

  function handleReroll() {
    if (!view) return;
    view.dispatch({ effects: rejectDiff.of() });
    if (streamRef.current) streamRef.current.abort();
    startStream(false);
  }

  if (!open) return null;
  return (
    <div className="cmdk-overlay" role="dialog" aria-label="Cmd+K inline edit">
      {blocked && (
        <div className="cmdk-dlp-warn">
          <div>
            Outbound content contains: <strong>{blocked.join(", ")}</strong>
          </div>
          <button type="button" onClick={() => startStream(true)}>
            Send anyway
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Instruct the model. Cmd+Enter to accept, Esc to reject."
          rows={2}
          disabled={streaming}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !streaming) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <div className="cmdk-controls">
          <button type="button" onClick={handleAccept} disabled={diffStatus !== "ready"}>
            Accept ⏎
          </button>
          <button type="button" onClick={handleReject}>
            Esc
          </button>
          <button type="button" onClick={handleReroll} disabled={!prompt.trim()}>
            ↻
          </button>
        </div>
      </form>
    </div>
  );
}
